// Blog Post: This Work Goes Deeper Than Just "Setting Better Boundaries"
import React from 'react';
import { Link } from 'react-router-dom';
import Navigation from '../components/Navigation.jsx';
import Footer from '../components/Footer.jsx';
function BlogDeeperThanBoundaries() {
  return <div className="min-h-screen bg-gradient-to-br from-sage-50 via-white to-warm-50">
      <Navigation variant="soft-brown" scrolled={true} />
      
      {/* Article Header */}
      <section className="px-4 sm:px-6 lg:px-8 pt-32 pb-12">
        <div className="max-w-4xl mx-auto">
          <Link to="/blog" className="inline-flex items-center text-sage-600 hover:text-sage-800 mb-8 transition-colors">
            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg><span>
            Back to Blog
          </span></Link>
          
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-serif font-light text-sage-800 mb-6">
            This Work Goes Deeper Than Just "Setting Better Boundaries"
          </h1>
          
          <div className="flex items-center text-gray-600 text-sm mb-8">
            <span>By Yasi Philipos</span>
            <span className="mx-3">•</span>
            <span>People-Pleasing & Nervous System Regulation</span>
          </div>
        </div>
      </section>

      {/* Article Content */}
      <section className="px-4 sm:px-6 lg:px-8 pb-20">
        <div className="max-w-4xl mx-auto">
          <div className="bg-white rounded-3xl p-8 md:p-12 shadow-lg">
            <p className="text-xl text-gray-700 mb-8 leading-relaxed">
              People-pleasing isn't a character flaw... it's a nervous system response called <span className="font-semibold italic">fawning</span>, where we put others' needs before our own to avoid conflict or feel safe in our relationships.
            </p>

            <p className="text-gray-700 mb-6 leading-relaxed">
              When we talk about boundaries, we're often told to "just say no" or "stand up for yourself." But if it were that simple, you would have done it already. The truth is, your body has learned that prioritizing others equals safety. And that's not something you can think your way out of... it's something you need to feel your way through.
            </p>

            <h2 className="text-3xl font-serif font-light text-sage-800 mt-12 mb-6">
              Understanding the Fawn Response
            </h2>

            <p className="text-gray-700 mb-6 leading-relaxed">
              The fawn response is one of the four survival responses (fight, flight, freeze, and fawn). When fighting back or running away doesn't feel safe, and freezing feels too vulnerable, your nervous system finds another way: appeasing. You become attuned to others' needs, hypervigilant to their emotions, and skilled at keeping the peace—often at the expense of your own well-being.
            </p>

            <p className="text-gray-700 mb-6 leading-relaxed">
              This response isn't a weakness. It's an intelligent adaptation. Your nervous system learned to protect you this way, and for a time, it worked. But now, you're ready for something different.
            </p>

            <h2 className="text-3xl font-serif font-light text-sage-800 mt-12 mb-6">
              Building a Felt Sense of Safety
            </h2>

            <p className="text-gray-700 mb-6 leading-relaxed">Using evidence-based, research-backed, neuroscience-informed approaches, we work to regulate your nervous system and build a felt sense of safety in your body.</p>

            <h2 className="text-3xl font-serif font-light text-sage-800 mt-12 mb-6">
              When Boundaries Become Natural
            </h2>

            <p className="text-gray-700 mb-6 leading-relaxed">
              As you begin to feel safe being fully yourself, setting kind, compassionate boundaries becomes easier. Not because you've memorized scripts or forced yourself to be assertive, but because your nervous system no longer equates authenticity with danger.
            </p>

            <p className="text-gray-700 mb-6 leading-relaxed">
              You start to notice when yes doesn't feel true. You pause before automatically agreeing. You speak up not from anger or resentment, but from a grounded place that honors both yourself and others.
            </p>

            <p className="text-gray-700 mb-8 leading-relaxed">
              Authenticity starts to feel natural. Not loud or confrontational—just true.
            </p>

            {/* Call to Action Box */}
            <div className="bg-gradient-to-br from-sage-50 to-warm-50 rounded-2xl p-8 mt-12 mb-8 border border-warm-200">
              <h3 className="text-2xl font-serif font-light text-sage-800 mb-4">
                Ready to Begin?
              </h3>
              <p className="text-gray-700 leading-relaxed mb-6">
                If you're tired of living on autopilot and ready to feel safe being yourself, I'm here to support you.
              </p>
              <Link to="/my-approach" className="inline-flex items-center text-warm-600 font-semibold hover:text-warm-700 transition-colors">
                <span>Learn more about my approach</span>
                <svg className="w-5 h-5 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                </svg>
              </Link>
            </div>

            {/* Related Articles */}
            <div className="border-t border-gray-200 pt-12 mt-12">
              <h3 className="text-xl font-serif font-light text-gray-800 mb-6">
                Related Articles
              </h3>
              <div className="space-y-4">
                <Link to="/blog/nervous-system-safety" className="block p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                  <h4 className="font-semibold text-sage-700 mb-1">Creating Nervous System Safety</h4>
                  <p className="text-sm text-gray-600">Understanding how to build felt safety in your body</p>
                </Link>
                <Link to="/blog/people-pleasing-coping-mechanism" className="block p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                  <h4 className="font-semibold text-sage-700 mb-1">People-Pleasing as a Coping Mechanism</h4>
                  <p className="text-sm text-gray-600">Why you can't just "stop" people-pleasing</p>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>;
}
export default BlogDeeperThanBoundaries;